import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import type { AvailableNetwork } from "../../types/pdm.types";

type CCTPDomainSelectorProps = {
  availableNetworks: AvailableNetwork[];
  selectedNetwork: string;
  value: string;
  onAccept: (value: string) => void;
  onCancel: () => void;
};

type CctpDomainItem = {
  network: string;
};

function isCctpDomainItem(value: unknown): value is CctpDomainItem {
  return (
    typeof value === "object" &&
    value !== null &&
    "network" in value &&
    typeof value.network === "string"
  );
}

function parseInitialSelection(value: string): string[] {
  if (!value.trim()) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(isCctpDomainItem).map((item) => item.network);
  } catch {
    return [];
  }
}

export function CCTPDomainSelector(props: CCTPDomainSelectorProps) {
  const { availableNetworks, selectedNetwork, value, onAccept, onCancel } = props;
  const selectableNetworks = useMemo(
    () => availableNetworks.filter((item) => item.id !== selectedNetwork),
    [availableNetworks, selectedNetwork],
  );
  const [selectedIds, setSelectedIds] = useState<string[]>(
    parseInitialSelection(value).filter((item) =>
      selectableNetworks.some((networkItem) => networkItem.id === item),
    ),
  );

  const handleToggle = (networkId: string) => {
    setSelectedIds((prev) =>
      prev.includes(networkId)
        ? prev.filter((item) => item !== networkId)
        : [...prev, networkId],
    );
  };

  const handleAccept = () => {
    const payload: CctpDomainItem[] = selectedIds.map((id) => ({ network: id }));
    onAccept(JSON.stringify(payload, null, 2));
  };

  return (
    <>
      <DialogContent dividers>
        {selectableNetworks.length === 0 ? (
          <Box sx={{ py: 2 }}>
            <Typography color="text.secondary">
              No additional networks are available for CCTP domains.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {selectableNetworks.map((item) => {
              const checked = selectedIds.includes(item.id);
              return (
                <ListItemButton key={item.id} onClick={() => handleToggle(item.id)}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText primary={item.name} secondary={item.id} />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          disabled={selectableNetworks.length === 0}
        >
          Accept
        </Button>
      </DialogActions>
    </>
  );
}
